import { RotateCcw, Undo2 } from 'lucide-react';
import { cn } from '@/lib/infra/utils';

/**
 * Per-section "Reset view" for the working reading view (#830).
 *
 * The reset changes ONLY the listed reading controls for its section (price
 * mode, side, filters, period, tab and similar). It never submits or replays
 * orders and never re-arms trading. It makes no promise beyond that: changing
 * a period or filter can legitimately close a panel or clear a focus through
 * the section's existing handlers, exactly as a manual change would. `scope`
 * is the exact per-section list, shown in the tooltip so the label stays short.
 *
 * One slot, two states. After a reset the same slot turns into "Undo reset"
 * until the trader changes anything (the hook invalidates Undo on the next
 * edit). Reset is not offered while Undo is available: at that moment the view
 * already equals its defaults, so a second Reset could only overwrite the one
 * useful Undo.
 *
 * Fixed footprint so the header never reflows when the label flips. Budget:
 * both labels are 10 characters at 11px UI type (~58–60px) plus a 12px icon,
 * 4px gap and 12px horizontal padding, ~88px; `w-24` (96px) leaves slack.
 * Confirm against the rendered screenshots rather than trusting the estimate.
 *
 * Sized to the section toolbar's chips (py-0.5, tiny type) so it reads as a
 * quiet action at the toolbar's right edge, not as another state chip.
 */
export function WorkingViewReset({
  label,
  scope = 'price mode, side and other reading choices',
  reset,
  undo,
  canUndo,
}: {
  /** Section name for the accessible label, e.g. "Game Lines". */
  label: string;
  /** Exactly what this reset changes, for the tooltip. */
  scope?: string;
  reset: () => void;
  undo: () => void;
  canUndo: boolean;
}) {
  const base =
    'flex w-24 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] leading-4 transition-colors ' +
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/70';
  if (canUndo) {
    return (
      <button
        type="button"
        onClick={undo}
        aria-label={`Undo ${label} view reset`}
        title={`Restore the ${label} reading controls you had before the reset`}
        className={cn(base, 'text-primary hover:bg-muted')}
      >
        <Undo2 className="h-3 w-3" aria-hidden="true" />
        Undo reset
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={reset}
      aria-label={`Reset ${label} view`}
      title={`Resets only these ${label} reading controls to their defaults: ${scope}. Never submits or replays orders, and never re-arms trading.`}
      className={cn(base, 'text-muted-foreground hover:bg-muted hover:text-foreground')}
    >
      <RotateCcw className="h-3 w-3" aria-hidden="true" />
      Reset view
    </button>
  );
}
